import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Header } from '../components/Header';
import { PackageCard } from '../components/PackageCard';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, ArrowRight, Clock, Users, ExternalLink, Plus, Minus, Sparkles, DollarSign, Gift, Trash2, Palette, Check } from 'lucide-react';
import { toast } from 'sonner';
import { DesignStudioModal } from '../components/DesignStudioModal';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { HelpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Catalog shown in the Custom Extras dropdown (fixed name + price)
const CUSTOM_EXTRA_CATALOG = [
  { id: 'helium_balloons',  name: 'Helium Balloons',        price: 8.99  },
  { id: 'cup_cakes',        name: 'Cup Cakes (Assorted)',   price: 6.99  },
  { id: 'cheese_cake',      name: 'Cheese Cake (Assorted)', price: 7.99  },
  { id: 'brownies',         name: 'Brownies',               price: 8.99  },
  { id: 'birthday_candles', name: 'Birthday Candles',       price: 3.99  },
  { id: 'bobie_the_bear',   name: 'Bobie the Bear',         price: 18.99 },
  { id: 'plushies',         name: 'Plushies',               price: 19.99 },
  { id: 'chocolate_pops',   name: 'Chocolate Pops',         price: 7.99  },
];

// Map of print-capable add-on ids → garment id used by the Design Studio
const PRINT_GARMENT_MAP = {
  print_cap: 'cap',
  print_tshirt: 'tshirt',
  print_mug: 'mug',
  byo_print: 'tshirt',
};

const ADD_ONS = [
  { id: 'diy_cake_custom', name: 'DIY Cake (Custom) — from $55', price: 55, unit: 'starting price', type: 'toggle', note: 'Order 1 week ahead; final price varies by design.' },
  { id: 'extra_30_min',    name: 'Extra 30 minutes',             price: 50, unit: 'flat',           type: 'toggle', note: 'Extend your party time beyond 120 minutes.' },
];

// iter-150: fixed set of catalog IDs that are treated as boolean toggles
// (they live in `selectedAddOns` rather than `extrasQty`). Everything else
// renders as a quantity stepper.
const TOGGLE_ITEM_IDS = new Set(ADD_ONS.map((a) => a.id));

// Per-child / per-item add-ons — quantity with sensible cap
const PREDEFINED_EXTRAS = [
  { id: 'print_cap',            name: 'Print a Cap (cap included)',        price: 19.00, max: 50, description: 'Custom-printed cap per child' },
  { id: 'print_tshirt',         name: 'Print a T-Shirt (shirt included)', price: 15.00, max: 50, description: 'Custom-printed T-shirt per child' },
  { id: 'print_mug',            name: 'Print a Mug',                       price: 15.00, max: 50, description: 'Ceramic mug with child\u2019s design' },
  { id: 'byo_print',            name: 'BYO Cap/T-Shirt — Printing only',   price: 5.00,  max: 50, description: 'Bring your own — we print on-site' },
  { id: 'extra_pizza_slice',    name: 'Extra Pizza Slice',                 price: 2.99,  max: 100, description: 'Cheese or pepperoni' },
  { id: 'goodie_bag_upgrade',   name: 'Goodie Bag Upgrade',                price: 6.00,  max: 50, description: 'Pre-filled with crafts, candy, small toy' },
  { id: 'gf_df_cupcake',        name: 'Gluten-Free / Dairy-Free Cupcake', price: 3.00,  max: 50, description: 'Allergy-friendly option' },
];

const steps = [
  { id: 1, title: 'Select Package', description: 'Choose your party package' },
  { id: 2, title: 'Add-Ons', description: 'Customize with extras, designs & more' },
  { id: 3, title: 'Pick Date & Time', description: 'Select your preferred date' },
  { id: 4, title: 'Guest Details', description: 'Tell us about the birthday child' },
  { id: 5, title: 'Your Information', description: 'Contact details' }
];

// iter-150 — Card component for a single add-on item on the customer page.
// Selected state (qty>0 or toggle=true) applies the pink outline + light bg.
const AddonItemCard = ({ item, isToggle, toggleValue, qtyValue, onToggle, onQtyChange }) => {
  const selected = isToggle ? !!toggleValue : (qtyValue > 0);
  const max = Number(item.max_qty) || 50;
  const priceLabel = item.unit_label === 'flat' ? 'flat' : `${item.unit_label || 'each'}`;
  return (
    <div
      className={`rounded-2xl border-2 transition-all p-3 flex flex-col gap-2 ${
        selected
          ? 'border-[#FF3B6B] bg-[#FFEDF3] shadow-sm'
          : 'border-[#E8E5DF] bg-white hover:border-[#FF6B6B]/40'
      }`}
      data-testid={`addon-card-${item.item_id}`}
    >
      <div className="w-full aspect-square rounded-xl overflow-hidden bg-[#FAF8F3] flex items-center justify-center">
        {item.image_url ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
            alt={item.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <span className="text-4xl">🎉</span>
        )}
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-[#1E1E24] leading-tight">{item.name}</p>
        {item.description && (
          <p className="text-[10px] text-[#6B6B7A] mt-0.5 line-clamp-2">{item.description}</p>
        )}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-[#FF3B6B] tabular-nums">
          ${Number(item.price || 0).toFixed(2)}
          <span className="text-[10px] text-[#6B6B7A] font-normal ml-1">{priceLabel}</span>
        </p>
        {isToggle ? (
          <button
            type="button"
            onClick={() => onToggle(!toggleValue)}
            className={`px-3 h-7 rounded-full text-xs font-semibold transition-colors ${
              toggleValue
                ? 'bg-[#FF3B6B] text-white'
                : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:border-[#FF3B6B]'
            }`}
            data-testid={`addon-toggle-${item.item_id}`}
          >
            {toggleValue ? '✓ Added' : '+ Add'}
          </button>
        ) : (
          <div className="flex items-center gap-1 bg-white rounded-full border border-[#E8E5DF] px-1 py-0.5">
            <button
              type="button"
              onClick={() => onQtyChange(Math.max(0, qtyValue - 1))}
              disabled={qtyValue <= 0}
              className="w-6 h-6 rounded-full flex items-center justify-center text-[#FF3B6B] disabled:text-[#D9D5CC] disabled:cursor-not-allowed hover:bg-[#FFEDF3]"
              data-testid={`addon-minus-${item.item_id}`}
            >
              <Minus className="w-3 h-3" />
            </button>
            <span className="text-sm font-bold tabular-nums w-6 text-center" data-testid={`addon-qty-${item.item_id}`}>
              {qtyValue}
            </span>
            <button
              type="button"
              onClick={() => onQtyChange(Math.min(max, qtyValue + 1))}
              disabled={qtyValue >= max}
              className="w-6 h-6 rounded-full flex items-center justify-center text-[#FF3B6B] disabled:text-[#D9D5CC] disabled:cursor-not-allowed hover:bg-[#FFEDF3]"
              data-testid={`addon-plus-${item.item_id}`}
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const BookingFlow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [packages, setPackages] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [guestCount, setGuestCount] = useState(1);
  const [childName, setChildName] = useState('');
  const [childAge, setChildAge] = useState('');
  const [edibleNotes, setEdibleNotes] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [extrasQty, setExtrasQty] = useState({}); // predefined extras: id -> qty
  const [customExtras, setCustomExtras] = useState([]); // [{id, catalog_id, name, unit_price, quantity}]
  // iter-150: dynamic catalog fetched from backend + collapse state
  const [catalog, setCatalog] = useState(null); // { categories: [{icon, name, items: [...]}] }
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [collapsedCats, setCollapsedCats] = useState({});
  const [showCustomExtraForm, setShowCustomExtraForm] = useState(false);
  const [customExtraDraft, setCustomExtraDraft] = useState({ name: '', unit_price: '', quantity: 1 });
  const [customChargeDesc, setCustomChargeDesc] = useState('');
  const [customChargeAmount, setCustomChargeAmount] = useState('');

  // Design Studio state
  const [designs, setDesigns] = useState([]); // [{garment, template_id|null, image_data_url|null, label}]
  const [designOpen, setDesignOpen] = useState(false);
  const [designInitialGarment, setDesignInitialGarment] = useState('tshirt');

  // Custom Extras picker state (dropdown)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerItemId, setPickerItemId] = useState('');
  const [pickerQty, setPickerQty] = useState(1);

  // Promo / discount code state
  const [promoInput, setPromoInput] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, type, value, amount, description }
  const [promoError, setPromoError] = useState('');

  // Gift card state (iter-92)
  const [giftCardInput, setGiftCardInput] = useState('');
  const [giftCardValidating, setGiftCardValidating] = useState(false);
  const [appliedGiftCard, setAppliedGiftCard] = useState(null); // { code, balance_remaining, amount, expires_at }
  const [giftCardError, setGiftCardError] = useState('');

  // Loyalty vouchers (fetched for logged-in customers)
  const [loyaltyVouchers, setLoyaltyVouchers] = useState([]); // active, unused
  const [loyaltyBalance, setLoyaltyBalance] = useState(0);
  const [appliedFreebieVoucher, setAppliedFreebieVoucher] = useState(null); // { voucher_id, reward_name, freebie_extra_id, unit_price }

  // Referral code state
  const [referralInput, setReferralInput] = useState('');
  const [referralValidating, setReferralValidating] = useState(false);
  const [appliedReferral, setAppliedReferral] = useState(null); // { code, referrer_first_name }
  const [referralError, setReferralError] = useState('');

  // Auto-running special offers (admin-managed via /crm/settings/special-offers).
  // Each offer has applies_to = 'all' | <package_id>. Customer never types a code.
  const [activeOffers, setActiveOffers] = useState([]); // raw list from backend

  useEffect(() => {
    fetchPackages();
    fetchTimeSlots();
    fetchLoyaltyVouchers();
    fetchActiveOffers();
    fetchAddonsCatalog();
  }, []);

  // iter-150 — fetch the admin-managed add-ons catalogue (no cache per spec)
  const fetchAddonsCatalog = async () => {
    setCatalogLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/addons/catalog`);
      setCatalog(res.data || { categories: [] });
    } catch {
      setCatalog({ categories: [] });
    } finally {
      setCatalogLoading(false);
    }
  };

  // Fetch the customer's loyalty vouchers (silent fail if not logged in)
  const fetchLoyaltyVouchers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customer/loyalty`, { withCredentials: true });
      setLoyaltyVouchers(res.data?.active_vouchers || []);
      setLoyaltyBalance(res.data?.balance || 0);
    } catch {
      // Not logged in — ignore silently
    }
  };

  // Pre-fill promo code from ?promo= query or the returning-prospect localStorage stash
  useEffect(() => {
    const qp = searchParams.get('promo');
    if (qp) { setPromoInput(qp); return; }
    try {
      const raw = localStorage.getItem('cb_prospect');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.inquiry_id) setPromoInput('WELCOMEBACK10');
      }
    } catch { /* ignore */ }
  }, [searchParams]);

  // Pre-fill referral code from ?ref= query
  useEffect(() => {
    const qr = searchParams.get('ref');
    if (qr && qr.trim()) setReferralInput(qr.trim().toUpperCase().slice(0, 20));
  }, [searchParams]);

  // Pre-fill gift card code from ?gift_card= query
  useEffect(() => {
    const gc = searchParams.get('gift_card');
    if (gc && gc.trim()) setGiftCardInput(gc.trim().toUpperCase().slice(0, 32));
  }, [searchParams]);

  // Pre-fill date + time from URL (hero availability calendar). When BOTH
  // date AND time_slot are present, we treat Step 2 (Pick Date & Time) as
  // already-completed: the booking flow visually skips it (the dot strip in
  // the header drops the step) and the user lands on Step 1 with a
  // "Selected: {date} at {time} · Change" pill.
  useEffect(() => {
    const qd = searchParams.get('date');
    const qt = searchParams.get('time_slot');
    if (qd && /^\d{4}-\d{2}-\d{2}$/.test(qd)) {
      const [y, m, d] = qd.split('-').map(Number);
      const preselected = new Date(y, m - 1, d);
      if (!isNaN(preselected.getTime())) {
        setSelectedDate(preselected);
      }
    }
    if (qt && /^\d{2}:\d{2}$/.test(qt)) {
      setSelectedTime(qt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dateTimePreSelected = Boolean(
    searchParams.get('date') && searchParams.get('time_slot'),
  );

  // Auto-apply once the code is pre-filled
  useEffect(() => {
    if (promoInput && !appliedPromo && !promoValidating) {
      validatePromo(promoInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoInput]);

  // Auto-validate gift card once pre-filled (from ?gift_card= or "Use" CTA)
  useEffect(() => {
    if (giftCardInput && !appliedGiftCard && !giftCardValidating) {
      validateGiftCard(giftCardInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftCardInput]);

  // Auto-validate referral code once pre-filled
  useEffect(() => {
    if (referralInput && !appliedReferral && !referralValidating) {
      validateReferral(referralInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralInput]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/packages`);
      setPackages(response.data);
    } catch (error) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeSlots = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/time-slots`);
      setTimeSlots(response.data);
    } catch (error) {
      console.error('Failed to load time slots');
    }
  };

  const fetchActiveOffers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/special-offers/active`);
      // Only include automatic offers (no promo_code) so the customer's
      // pricing summary updates in real time without them typing anything.
      setActiveOffers((response.data || []).filter((o) => !o.promo_code));
    } catch (e) {
      // Silent — offers are a non-critical enhancement.
      setActiveOffers([]);
    }
  };

  // Pick the SINGLE best automatic offer for the given package id (greatest
  // savings on the package's base price). Used by both the package badge
  // and the booking-summary sidebar so customer sees the same number.
  const offerForPackage = (pkg) => {
    if (!pkg || !activeOffers || activeOffers.length === 0) return null;
    let best = null;
    let bestAmount = 0;
    for (const o of activeOffers) {
      if (o.applies_to !== 'all' && o.applies_to !== pkg.id) continue;
      const amt = o.discount_type === 'percent'
        ? (Number(pkg.price || 0) * Number(o.discount_value || 0)) / 100
        : Math.min(Number(o.discount_value || 0), Number(pkg.price || 0));
      if (amt > bestAmount) { bestAmount = amt; best = { ...o, amount: Math.round(amt * 100) / 100 }; }
    }
    return best;
  };

  const fetchAvailableSlots = async (date) => {
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await axios.get(`${API_URL}/api/bookings/available-slots/${dateStr}`);
      const avail = response.data.available_slots || [];
      setAvailableSlots(avail);
      // Only clear selectedTime if it's no longer available for this date.
      // This preserves the time slot pre-selected from the hero availability
      // calendar (?date=…&time_slot=…) and stops the "Invalid time slot"
      // submission bug caused by wiping the URL-prefilled time.
      setSelectedTime((prev) => (prev && avail.includes(prev) ? prev : ''));
    } catch (error) {
      console.error('Failed to load available slots');
    }
  };

  const scrollToTop = () => {
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { window.scrollTo(0, 0); }
  };

  // Validate that all requirements up to and including `targetStep` are met.
  // Used by step-indicator clicks + Next button.
  const canGoToStep = (targetStep) => {
    // Forward-only validation; steps behind the current one are always reachable.
    if (targetStep <= currentStep) return { ok: true };
    // Package must be picked before any subsequent step
    if (targetStep >= 2 && !selectedPackage) return { ok: false, msg: 'Please select a package first' };
    // Date & time required before guests / your-info steps
    if (targetStep >= 4 && (!selectedDate || !selectedTime))
      return { ok: false, msg: 'Please pick a date and time' };
    // Guest details required before your-info
    if (targetStep >= 5 && (!childName || !childAge || guestCount < 1))
      return { ok: false, msg: 'Please fill in guest details' };
    return { ok: true };
  };

  const goToStep = (targetStep) => {
    if (targetStep === currentStep) return;
    const check = canGoToStep(targetStep);
    if (!check.ok) { toast.error(check.msg); return; }
    // Honour the "date+time pre-selected from hero modal" skip: step 3 is hidden
    if (dateTimePreSelected && targetStep === 3) return;
    setCurrentStep(targetStep);
    scrollToTop();
  };

  const handleNext = () => {
    if (currentStep === 1 && !selectedPackage) {
      toast.error('Please select a package');
      return;
    }
    if (currentStep === 3 && (!selectedDate || !selectedTime)) {
      toast.error('Please select date and time');
      return;
    }
    if (currentStep === 4 && (!childName || !childAge || guestCount < 1)) {
      toast.error('Please fill in all guest details');
      return;
    }
    if (currentStep < 5) {
      // When date+time were pre-picked from the hero modal, skip Step 3 (Date & Time)
      let next = currentStep + 1;
      if (dateTimePreSelected && next === 3) next = 4;
      setCurrentStep(next);
      scrollToTop();
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // Mirror the forward-skip when date+time were locked from the modal
      let prev = currentStep - 1;
      if (dateTimePreSelected && prev === 3) prev = 2;
      setCurrentStep(prev);
      scrollToTop();
    }
  };

  const handleSubmit = async () => {
    if (!customerName || !customerEmail || !customerPhone) {
      toast.error('Please fill in all contact details');
      return;
    }

    // Safety net: if the time slot got cleared in state (e.g. a re-fetch raced
    // the URL-prefill effect) but it's still present in the URL we used to
    // start this flow, restore it before submitting. Prevents the spurious
    // "Invalid time slot" error users hit after the hero "Check Availability"
    // modal pre-picks a date+time.
    let finalTimeSlot = selectedTime;
    if (!finalTimeSlot) {
      const qt = searchParams.get('time_slot');
      if (qt && /^\d{2}:\d{2}$/.test(qt)) {
        finalTimeSlot = qt;
        setSelectedTime(qt);
      }
    }
    if (!selectedDate || !finalTimeSlot) {
      toast.error('Please pick a date and time before submitting');
      setCurrentStep(3);
      return;
    }

    try {
      // Build predefined_extras list from the loaded catalog (iter-150).
      // Any qty > 0 in extrasQty is included with its live catalog price.
      const catalogItemsFlat = (catalog?.categories || [])
        .flatMap((c) => c.items || [])
        .filter((it) => !TOGGLE_ITEM_IDS.has(it.item_id));
      const _catalogById = Object.fromEntries(catalogItemsFlat.map((it) => [it.item_id, it]));
      // Fallback to legacy hardcoded array for BC (e.g., loyalty freebies applied
      // before catalog loaded).
      PREDEFINED_EXTRAS.forEach((it) => { if (!_catalogById[it.id]) _catalogById[it.id] = it; });
      const predefinedExtrasForSubmit = Object.entries(extrasQty)
        .filter(([, q]) => (q || 0) > 0)
        .map(([id, q]) => {
          const item = _catalogById[id];
          if (!item) return null;
          return {
            id,
            name: item.name,
            quantity: q,
            price: Number(item.price),
          };
        })
        .filter(Boolean);

      const existingAddOns = Object.entries(selectedAddOns)
        .filter(([_, v]) => v && v !== 0)
        .map(([id, v]) => {
          const addon = ADD_ONS.find(a => a.id === id);
          const qty = typeof v === 'number' ? v : (addon?.type === 'per_guest' ? guestCount : 1);
          return { id, name: addon?.name, quantity: qty, price: addon?.price };
        });

      const customExtrasForSubmit = customExtras
        .filter(e => (parseFloat(e.unit_price) || 0) > 0 && (parseInt(e.quantity, 10) || 0) > 0 && (e.name || '').trim())
        .map(e => ({
          name: e.name.trim().slice(0, 50),
          unit_price: parseFloat(e.unit_price),
          quantity: parseInt(e.quantity, 10),
          catalog_id: e.catalog_id || null,
        }));

      const miscCharges = customChargeAmount && parseFloat(customChargeAmount) > 0 ? [{
        name: customChargeDesc || 'Custom charge',
        unit_price: parseFloat(customChargeAmount),
        quantity: 1,
      }] : [];

      const bookingData = {
        package_id: selectedPackage.id,
        date: selectedDate.toISOString().split('T')[0],
        time_slot: finalTimeSlot,
        guest_count: guestCount,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        child_name: childName,
        child_age: parseInt(childAge),
        edible_notes: edibleNotes || null,
        add_ons: [...existingAddOns, ...predefinedExtrasForSubmit],
        custom_charges: [...customExtrasForSubmit, ...miscCharges],
        designs: designs.map(d => ({
          garment: d.garment,
          template_id: d.template_id || null,
          image_data_url: d.image_data_url || null,
          label: d.label || '',
        })),
        discount_code: appliedPromo?.code || null,
        loyalty_voucher_id: appliedFreebieVoucher?.voucher_id || null,
        referral_code: appliedReferral?.code || null,
        gift_card_code: appliedGiftCard?.code || null,
      };

      const response = await axios.post(`${API_URL}/api/bookings`, bookingData, { withCredentials: true });
      toast.success('Booking created successfully!');
      navigate(`/booking/${response.data.booking_id}/allergy-form`);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to create booking';
      // If the slot was just taken (race) or the slot value is somehow invalid,
      // bounce the user back to the date/time picker so they can re-select.
      if (/time slot/i.test(detail)) {
        toast.error(`${detail}. Please pick another time.`);
        // Clear the URL-prefill so step 2 isn't skipped on the retry
        if (selectedDate) {
          await fetchAvailableSlots(selectedDate);
        }
        // Force the user to re-pick a time — the old one is now stale.
        setSelectedTime('');
        navigate('/book', { replace: true });
        setCurrentStep(2);
        return;
      }
      toast.error(detail);
    }
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 90);
    return date < today || date > maxDate;
  };

  const calculateTotal = () => {
    if (!selectedPackage) return { subtotal: 0, tax: 0, total: 0, deposit: 0, addOnsTotal: 0, extrasTotal: 0, customExtrasTotal: 0, discountAmount: 0, preDiscountSubtotal: 0, freebieAmount: 0, specialOfferAmount: 0, specialOffer: null };
    let subtotal = selectedPackage.price;

    // Calculate add-ons
    let addOnsTotal = 0;
    ADD_ONS.forEach(addon => {
      const val = selectedAddOns[addon.id];
      if (!val) return;
      if (addon.type === 'toggle' && val === true) {
        addOnsTotal += addon.price;
      } else if (addon.type === 'quantity' && typeof val === 'number' && val > 0) {
        addOnsTotal += addon.price * val;
      } else if (addon.type === 'per_guest' && val === true) {
        addOnsTotal += addon.price * guestCount;
      }
    });

    subtotal += addOnsTotal;

    // Predefined extras — prefer the fetched catalog (iter-150) so admin-added
    // items get priced correctly. Fall back to legacy PREDEFINED_EXTRAS array
    // if the catalog hasn't loaded yet (e.g., initial render before fetch).
    let extrasTotal = 0;
    const catalogItems = (catalog?.categories || [])
      .flatMap((c) => c.items || [])
      .filter((it) => !TOGGLE_ITEM_IDS.has(it.item_id));
    if (catalogItems.length > 0) {
      catalogItems.forEach((it) => {
        const q = extrasQty[it.item_id] || 0;
        if (q > 0) extrasTotal += Number(it.price) * q;
      });
    } else {
      PREDEFINED_EXTRAS.forEach((item) => {
        const q = extrasQty[item.id] || 0;
        if (q > 0) extrasTotal += item.price * q;
      });
    }
    subtotal += extrasTotal;

    // Custom extras (user-defined)
    const customExtrasTotal = customExtras.reduce((sum, e) => {
      const p = parseFloat(e.unit_price) || 0;
      const q = parseInt(e.quantity, 10) || 0;
      return sum + (p > 0 && q > 0 ? p * q : 0);
    }, 0);
    subtotal += customExtrasTotal;

    // Manual misc custom charge
    const customAmount = parseFloat(customChargeAmount) || 0;
    subtotal += customAmount;

    const preDiscountSubtotal = subtotal;

    // Apply automatic special offer (no code) — same rules as backend:
    // does NOT stack with a promo code, only one offer applied (best one).
    let specialOfferAmount = 0;
    let specialOffer = null;
    if (!appliedPromo && preDiscountSubtotal > 0) {
      const off = offerForPackage(selectedPackage);
      if (off) {
        // Recalculate amount against ACTUAL subtotal (price + addons + extras)
        // so banner shows the real savings, not just package savings.
        const amt = off.discount_type === 'percent'
          ? Math.round(preDiscountSubtotal * (Number(off.discount_value) / 100) * 100) / 100
          : Math.min(Number(off.discount_value), preDiscountSubtotal);
        if (amt > 0) {
          specialOfferAmount = amt;
          specialOffer = { ...off, amount: amt };
          subtotal = Math.max(0, preDiscountSubtotal - amt);
        }
      }
    }

    // Apply promo discount (pre-tax) — replaces auto-offer when both exist.
    let discountAmount = 0;
    if (appliedPromo && preDiscountSubtotal > 0) {
      if (appliedPromo.type === 'percent') {
        discountAmount = Math.round(preDiscountSubtotal * (appliedPromo.value / 100) * 100) / 100;
      } else {
        discountAmount = Math.min(appliedPromo.value, preDiscountSubtotal);
      }
      subtotal = Math.max(0, preDiscountSubtotal - discountAmount);
    }

    // Apply loyalty freebie voucher (pre-tax) — 1 free unit of matching extra.
    // Only applies when the matching extra is actually in cart (qty>=1).
    let freebieAmount = 0;
    if (appliedFreebieVoucher) {
      const fid = appliedFreebieVoucher.freebie_extra_id;
      const pre = PREDEFINED_EXTRAS.find(x => x.id === fid);
      const cust = CUSTOM_EXTRA_CATALOG.find(x => x.id === fid);
      const inPredefined = pre && (extrasQty[fid] || 0) >= 1;
      const inCustom = cust && customExtras.some(e => e.catalog_id === fid && (parseInt(e.quantity, 10) || 0) >= 1);
      if (inPredefined || inCustom) {
        freebieAmount = Math.min(appliedFreebieVoucher.unit_price || pre?.price || cust?.price || 0, subtotal);
        subtotal = Math.max(0, Math.round((subtotal - freebieAmount) * 100) / 100);
      }
    }

    // Apply gift card (pre-tax) — applies up to remaining balance OR subtotal,
    // whichever is smaller. The backend re-applies this for the authoritative
    // value, but we mirror the math here so the summary is accurate.
    let giftCardAmount = 0;
    if (appliedGiftCard && subtotal > 0) {
      giftCardAmount = Math.min(appliedGiftCard.balance_remaining || 0, subtotal);
      giftCardAmount = Math.round(giftCardAmount * 100) / 100;
      subtotal = Math.max(0, Math.round((subtotal - giftCardAmount) * 100) / 100);
    }

    const tax = subtotal * 0.13;
    const total = subtotal + tax;
    const deposit = total * 0.5;
    return { subtotal, tax, total, deposit, addOnsTotal, extrasTotal, customExtrasTotal, discountAmount, preDiscountSubtotal, freebieAmount, specialOfferAmount, specialOffer, giftCardAmount };
  };

  const toggleAddOn = (addonId) => {
    setSelectedAddOns(prev => ({ ...prev, [addonId]: !prev[addonId] }));
  };

  const setAddOnQty = (addonId, qty) => {
    setSelectedAddOns(prev => ({ ...prev, [addonId]: Math.max(0, qty) }));
  };

  const setExtraQty = (id, qty, max) => {
    const safe = Math.min(Math.max(0, qty || 0), max);
    setExtrasQty(prev => ({ ...prev, [id]: safe }));
  };

  const validatePromo = async (codeArg) => {    const code = ((codeArg ?? promoInput) || '').trim().toUpperCase();
    if (!code) { setPromoError('Enter a code first'); return; }
    setPromoValidating(true);
    setPromoError('');
    try {
      // We only need a positive subtotal for backend validation — amount recomputed client-side anyway.
      const res = await axios.post(`${API_URL}/api/discounts/validate`, {
        code,
        customer_email: customerEmail || undefined,
        subtotal: 100,
      });
      if (res.data?.valid) {
        setAppliedPromo({
          code: res.data.code,
          type: res.data.type,
          value: res.data.value,
          description: res.data.description,
        });
        toast.success(`Promo ${res.data.code} applied`);
      } else {
        setAppliedPromo(null);
        setPromoError(res.data?.reason || 'Invalid code');
      }
    } catch (e) {
      setAppliedPromo(null);
      setPromoError(e.response?.data?.detail || 'Could not validate code');
    } finally {
      setPromoValidating(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
    setPromoError('');
  };

  // ---- Gift card (iter-92) ----
  const validateGiftCard = async (codeArg) => {
    const code = ((codeArg ?? giftCardInput) || '').trim().toUpperCase();
    if (!code) { setGiftCardError('Enter a code first'); return; }
    setGiftCardValidating(true);
    setGiftCardError('');
    try {
      const res = await axios.post(`${API_URL}/api/gift-cards/validate`, { code });
      if (res.data?.valid) {
        setAppliedGiftCard({
          code: res.data.code,
          amount: res.data.amount,
          balance_remaining: res.data.balance_remaining,
          expires_at: res.data.expires_at,
        });
        toast.success(`Gift card ${res.data.code} ($${res.data.balance_remaining.toFixed(2)} available) applied`);
      } else {
        setAppliedGiftCard(null);
        setGiftCardError('Invalid code');
      }
    } catch (e) {
      setAppliedGiftCard(null);
      setGiftCardError(e.response?.data?.detail || 'Could not validate code');
    } finally {
      setGiftCardValidating(false);
    }
  };

  const removeGiftCard = () => {
    setAppliedGiftCard(null);
    setGiftCardInput('');
    setGiftCardError('');
  };

  // ---- Loyalty vouchers ----
  const applyDiscountVoucher = (v) => {
    if (!v?.discount_code) return;
    setPromoInput(v.discount_code);
    validatePromo(v.discount_code);
  };

  const applyFreebieVoucher = (v) => {
    if (!v?.freebie_extra_id) return;
    const fid = v.freebie_extra_id;
    const pre = PREDEFINED_EXTRAS.find(x => x.id === fid);
    const cust = CUSTOM_EXTRA_CATALOG.find(x => x.id === fid);
    if (!pre && !cust) {
      toast.error('This freebie item is not in our current catalog');
      return;
    }
    // Ensure qty >= 1 of the matching item is in cart
    if (pre) {
      setExtrasQty(prev => ({ ...prev, [fid]: Math.max(prev[fid] || 0, 1) }));
    } else if (cust) {
      setCustomExtras(prev => {
        const idx = prev.findIndex(e => e.catalog_id === cust.id);
        if (idx !== -1) {
          if ((parseInt(prev[idx].quantity, 10) || 0) < 1) {
            return prev.map((e, i) => i === idx ? { ...e, quantity: 1 } : e);
          }
          return prev;
        }
        return [...prev, {
          id: `ce_${Date.now()}`,
          catalog_id: cust.id,
          name: cust.name,
          unit_price: cust.price,
          quantity: 1,
        }];
      });
    }
    setAppliedFreebieVoucher({
      voucher_id: v.voucher_id,
      reward_name: v.reward_name,
      freebie_extra_id: fid,
      unit_price: pre?.price || cust?.price || 0,
    });
    toast.success(`"${v.reward_name}" will be applied at checkout`);
  };

  const removeFreebieVoucher = () => setAppliedFreebieVoucher(null);

  // ---- Referral code ----
  const validateReferral = async (codeArg) => {
    const code = ((codeArg ?? referralInput) || '').trim().toUpperCase();
    if (!code) { setReferralError('Enter a code first'); return; }
    setReferralValidating(true);
    setReferralError('');
    try {
      const res = await axios.post(`${API_URL}/api/public/referral/validate`, {
        code,
        customer_email: customerEmail || undefined,
      });
      if (res.data?.valid) {
        setAppliedReferral({ code, referrer_first_name: res.data.referrer_first_name });
        toast.success(`Referred by ${res.data.referrer_first_name} — they'll earn points when you book!`);
      } else {
        setAppliedReferral(null);
        setReferralError(res.data?.reason || 'Invalid code');
      }
    } catch (e) {
      setAppliedReferral(null);
      setReferralError(e.response?.data?.detail || 'Could not validate code');
    } finally {
      setReferralValidating(false);
    }
  };

  const removeReferral = () => {
    setAppliedReferral(null);
    setReferralInput('');
    setReferralError('');
  };

  const addCustomExtra = () => {
    // Open the dropdown picker instead of adding an empty free-text row
    setPickerItemId('');
    setPickerQty(1);
    setPickerOpen(true);
  };

  const confirmAddCustomExtra = () => {
    const item = CUSTOM_EXTRA_CATALOG.find(c => c.id === pickerItemId);
    if (!item) { toast.error('Pick an item first'); return; }
    const q = Math.max(1, Math.min(parseInt(pickerQty, 10) || 1, 50));
    // If the same item was already added, bump its qty instead of duplicating
    const idx = customExtras.findIndex(e => e.catalog_id === item.id);
    if (idx !== -1) {
      setCustomExtras(prev => prev.map((e, i) => (i === idx ? { ...e, quantity: (parseInt(e.quantity, 10) || 0) + q } : e)));
    } else {
      setCustomExtras(prev => [
        ...prev,
        { id: `ce_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, catalog_id: item.id, name: item.name, unit_price: item.price, quantity: q },
      ]);
    }
    setPickerOpen(false);
    setPickerItemId('');
    setPickerQty(1);
  };

  const updateCustomExtraQty = (id, q) => {
    const safe = Math.max(1, Math.min(parseInt(q, 10) || 1, 50));
    setCustomExtras(prev => prev.map(e => (e.id === id ? { ...e, quantity: safe } : e)));
  };

  const removeCustomExtra = (id) => {
    setCustomExtras(prev => prev.filter(e => e.id !== id));
  };

  // Design Studio helpers
  const openDesignStudio = (garment = 'tshirt') => {
    setDesignInitialGarment(garment);
    setDesignOpen(true);
  };
  const onDesignSaved = (design) => {
    setDesigns(prev => [...prev, { ...design, id: `design_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` }]);
    toast.success('Design saved to your booking');
  };
  const removeDesign = (id) => setDesigns(prev => prev.filter(d => d.id !== id));

  const pricing = calculateTotal();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF8]">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />

      <div className="container mx-auto px-4 py-8">
        {/* Progress Steps — when date+time were pre-picked from the hero modal, hide
             Step 3 (Date & Time) from the visual stepper since the user never actually visits it. */}
        <div className="mb-12">
          <div className="flex justify-between items-center max-w-4xl mx-auto">
            {steps.filter((s) => !(dateTimePreSelected && s.id === 3)).map((step, index, arr) => {
              const visibleIndex = index + 1;   // 1-based rank in the visible stepper
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              const isReachable = canGoToStep(step.id).ok;
              return (
                <div key={step.id} className="flex items-center flex-1 last:flex-none">
                  <button
                    type="button"
                    onClick={() => goToStep(step.id)}
                    disabled={!isReachable && !isCompleted && !isActive}
                    className={`flex flex-col items-center transition-transform ${
                      (isReachable || isCompleted || isActive) ? 'cursor-pointer hover:scale-105' : 'cursor-not-allowed opacity-60'
                    }`}
                    aria-label={`Go to step ${step.title}`}
                    data-testid={`step-indicator-${step.id}`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                        isActive
                          ? 'bg-[#FF3B6B] text-white ring-4 ring-[#FF3B6B]/20'
                          : isCompleted
                          ? 'bg-[#0FAB66] text-white'
                          : 'bg-[#E8E5DF] text-[#6B6B7A]'
                      }`}
                    >
                      {isCompleted ? <Check className="w-5 h-5" /> : visibleIndex}
                    </div>
                    <span
                      className={`text-xs mt-2 hidden sm:block ${
                        isActive ? 'text-[#1E1E24] font-semibold' : isCompleted ? 'text-[#0FAB66]' : 'text-[#6B6B7A]'
                      }`}
                    >
                      {step.title}
                    </span>
                  </button>
                  {index < arr.length - 1 && (
                    <div className={`flex-1 min-w-[24px] sm:min-w-[64px] h-1 mx-2 rounded-full ${
                      currentStep > step.id ? 'bg-[#0FAB66]' : 'bg-[#E8E5DF]'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl border border-[#E8E5DF] p-6 sm:p-8"
            >
              {/* Step 1: Package Selection */}
              {currentStep === 1 && (
                <div>
                  <h2 className="text-2xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Choose Your Package
                  </h2>
                  <p className="text-[#6B6B7A] mb-4">All packages include DIY fruit bouquet + bracelet/keychain stations and 120 minutes of party time. Food varies by package — add pizza, cupcakes or a custom cake from the add-ons.</p>

                  {selectedDate && !dateTimePreSelected && (
                    <div
                      className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#06D6A0]/10 border border-[#06D6A0]/30 text-sm text-[#0B8F6A]"
                      data-testid="step1-date-locked-banner"
                    >
                      <Clock className="w-4 h-4" />
                      Date selected: <strong>{selectedDate.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</strong>
                      <span className="text-[#6B6B7A]">· time picked next</span>
                    </div>
                  )}
                  {dateTimePreSelected && selectedDate && selectedTime && (
                    <div
                      className="mb-6 flex flex-wrap items-center gap-2 px-4 py-2 rounded-2xl bg-[#06D6A0]/10 border border-[#06D6A0]/30 text-sm text-[#0B8F6A]"
                      data-testid="step1-datetime-pill"
                    >
                      <Clock className="w-4 h-4" />
                      <span>
                        Selected:{' '}
                        <strong>{selectedDate.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</strong>{' '}
                        at <strong>{({ '10:00': '10 AM', '13:00': '1 PM', '16:00': '4 PM' }[selectedTime]) || selectedTime}</strong>
                      </span>
                      <button
                        type="button"
                        onClick={() => navigate('/')}
                        className="ml-auto text-xs font-semibold text-[#FF3B6B] hover:underline"
                        data-testid="step1-change-datetime"
                      >
                        Change date/time
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {packages.map((pkg, index) => (
                      <PackageCard 
                        key={pkg.id}
                        pkg={pkg}
                        isSelected={selectedPackage?.id === pkg.id}
                        onSelect={() => setSelectedPackage(pkg)}
                        index={index}
                        offer={offerForPackage(pkg)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Add-Ons — customize with extras, designs, custom charges */}
              {currentStep === 2 && (
                <div data-testid="step-addons-page" className="pb-32">
                  <h2 className="text-2xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Customize Your Party
                  </h2>
                  <p className="text-[#6B6B7A] mb-6">
                    Enhance {selectedPackage?.name || 'your package'} with add-ons, extras, designs and more. Skip if you'd like to keep it simple.
                  </p>
                  {!selectedPackage && (
                    <div className="p-4 mb-6 rounded-xl bg-[#FFE8E5] border border-[#D4302A] text-sm text-[#D4302A]">
                      Please go back and select a package first.
                    </div>
                  )}

                  {selectedPackage && catalogLoading && (
                    <div className="py-16 flex flex-col items-center gap-2 text-[#6B6B7A]">
                      <div className="w-8 h-8 border-4 border-[#FF3B6B] border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm">Loading add-on menu…</span>
                    </div>
                  )}

                  {selectedPackage && !catalogLoading && (catalog?.categories || []).map((cat) => {
                    const isCollapsed = !!collapsedCats[cat.category_id];
                    // Total qty across this category to show a badge
                    const catQty = (cat.items || []).reduce((sum, it) => {
                      if (TOGGLE_ITEM_IDS.has(it.item_id)) return sum + (selectedAddOns[it.item_id] ? 1 : 0);
                      return sum + (extrasQty[it.item_id] || 0);
                    }, 0);
                    if ((cat.items || []).length === 0) return null;
                    return (
                      <div key={cat.category_id} className="mb-4 rounded-2xl bg-white border border-[#E8E5DF] overflow-hidden"
                        data-testid={`addon-cat-${cat.category_id}`}>
                        <button
                          type="button"
                          onClick={() => setCollapsedCats((s) => ({ ...s, [cat.category_id]: !s[cat.category_id] }))}
                          className="w-full flex items-center gap-3 px-5 py-4 hover:bg-[#FFFDF8] transition-colors"
                        >
                          <span className="text-2xl">{cat.icon}</span>
                          <h3 className="text-lg font-bold text-[#1E1E24] flex-1 text-left" style={{ fontFamily: 'Fredoka' }}>
                            {cat.name}
                          </h3>
                          {catQty > 0 && (
                            <Badge className="bg-[#FF3B6B] hover:bg-[#FF3B6B] text-white rounded-full text-[10px]">
                              {catQty} selected
                            </Badge>
                          )}
                          <span className="text-[#6B6B7A]">
                            {isCollapsed ? <Plus className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                          </span>
                        </button>
                        {!isCollapsed && (
                          <div className="px-5 pb-5 pt-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {cat.items.map((it) => (
                              <AddonItemCard
                                key={it.item_id}
                                item={it}
                                isToggle={TOGGLE_ITEM_IDS.has(it.item_id)}
                                toggleValue={!!selectedAddOns[it.item_id]}
                                qtyValue={extrasQty[it.item_id] || 0}
                                onToggle={(v) => setSelectedAddOns((s) => ({ ...s, [it.item_id]: v }))}
                                onQtyChange={(q) => setExtrasQty((s) => ({ ...s, [it.item_id]: q }))}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Custom Extras — user-defined items on the fly */}
                  {selectedPackage && !catalogLoading && (
                    <div className="mb-4 rounded-2xl bg-white border border-[#E8E5DF] overflow-hidden">
                      <div className="flex items-center gap-3 px-5 py-4 border-b border-[#F0EDE6]">
                        <span className="text-2xl">➕</span>
                        <h3 className="text-lg font-bold text-[#1E1E24] flex-1" style={{ fontFamily: 'Fredoka' }}>
                          Custom Extras
                        </h3>
                        {customExtras.length > 0 && (
                          <Badge className="bg-[#FF3B6B] hover:bg-[#FF3B6B] text-white rounded-full text-[10px]">
                            {customExtras.length} added
                          </Badge>
                        )}
                      </div>
                      <div className="p-5 space-y-3">
                        <p className="text-xs text-[#6B6B7A]">
                          Not seeing what you need? Add anything else — extra hour, custom fruit tray, cleaning fee, host TIP, etc.
                        </p>

                        {customExtras.length > 0 && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {customExtras.map((ce) => (
                              <div key={ce.id} className="flex items-center gap-3 bg-[#FFEDF3] border border-[#FF3B6B] rounded-xl p-3"
                                data-testid={`custom-extra-${ce.id}`}>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{ce.name}</p>
                                  <p className="text-[11px] text-[#6B6B7A]">
                                    {ce.quantity} × ${Number(ce.unit_price || 0).toFixed(2)} = <strong className="text-[#FF3B6B]">${(Number(ce.unit_price || 0) * Number(ce.quantity || 0)).toFixed(2)}</strong>
                                  </p>
                                </div>
                                <Button
                                  type="button" size="sm" variant="ghost"
                                  onClick={() => setCustomExtras((arr) => arr.filter((x) => x.id !== ce.id))}
                                  className="h-7 w-7 p-0 text-[#D4302A]"
                                  data-testid={`custom-extra-remove-${ce.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}

                        {!showCustomExtraForm ? (
                          <Button
                            type="button" size="sm" variant="outline"
                            onClick={() => { setShowCustomExtraForm(true); setCustomExtraDraft({ name: '', unit_price: '', quantity: 1 }); }}
                            className="rounded-full border-dashed border-[#FF3B6B] text-[#FF3B6B] hover:bg-[#FFEDF3]"
                            data-testid="add-custom-extra-btn"
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" /> Add Custom Extra
                          </Button>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_100px_auto] gap-2 items-end p-3 bg-[#FFFDF8] border border-[#E8E5DF] rounded-xl">
                            <div>
                              <label className="block text-[10px] uppercase text-[#6B6B7A] mb-1">Item name</label>
                              <Input value={customExtraDraft.name}
                                onChange={(e) => setCustomExtraDraft((d) => ({ ...d, name: e.target.value }))}
                                placeholder="Extra hour, host TIP…"
                                className="h-9 rounded-xl border-[#E8E5DF] text-sm"
                                data-testid="custom-extra-name-input" />
                            </div>
                            <div>
                              <label className="block text-[10px] uppercase text-[#6B6B7A] mb-1">Price ($)</label>
                              <Input type="number" step="0.01" min="0" value={customExtraDraft.unit_price}
                                onChange={(e) => setCustomExtraDraft((d) => ({ ...d, unit_price: e.target.value }))}
                                placeholder="0.00"
                                className="h-9 rounded-xl border-[#E8E5DF] text-sm text-right"
                                data-testid="custom-extra-price-input" />
                            </div>
                            <div>
                              <label className="block text-[10px] uppercase text-[#6B6B7A] mb-1">Qty</label>
                              <Input type="number" min="1" max="99" value={customExtraDraft.quantity}
                                onChange={(e) => setCustomExtraDraft((d) => ({ ...d, quantity: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                                className="h-9 rounded-xl border-[#E8E5DF] text-sm text-right"
                                data-testid="custom-extra-qty-input" />
                            </div>
                            <div className="flex gap-1">
                              <Button
                                type="button" size="sm"
                                onClick={() => {
                                  const name = (customExtraDraft.name || '').trim();
                                  const price = parseFloat(customExtraDraft.unit_price);
                                  const q = parseInt(customExtraDraft.quantity, 10) || 1;
                                  if (!name) { toast.error('Item name is required'); return; }
                                  if (!Number.isFinite(price) || price <= 0) { toast.error('Price must be > 0'); return; }
                                  const id = `custom_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
                                  setCustomExtras((arr) => [...arr, { id, name, unit_price: price, quantity: q }]);
                                  setShowCustomExtraForm(false);
                                }}
                                className="h-9 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-xs"
                                data-testid="custom-extra-add-confirm"
                              >
                                Add
                              </Button>
                              <Button type="button" size="sm" variant="outline"
                                onClick={() => setShowCustomExtraForm(false)}
                                className="h-9 rounded-full text-xs">
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Misc charge (kept from old flow for BC) */}
                  {selectedPackage && !catalogLoading && (
                    <div className="mb-4 rounded-2xl bg-white border border-[#E8E5DF] p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-2xl">💵</span>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
                            Misc Charge (optional)
                          </h3>
                          <p className="text-[11px] text-[#6B6B7A]">One-line note + amount, no quantity.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-2">
                        <Input value={customChargeDesc} onChange={e => setCustomChargeDesc(e.target.value)}
                          placeholder="Description (e.g. Balloon Arch, Damage Deposit)"
                          className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="custom-charge-desc" />
                        <Input type="number" step="0.01" min="0" value={customChargeAmount}
                          onChange={e => setCustomChargeAmount(e.target.value)}
                          placeholder="$ Amount"
                          className="h-9 rounded-xl border-[#E8E5DF] text-sm text-right" data-testid="custom-charge-amount" />
                      </div>
                    </div>
                  )}

                  {/* Sticky Persistent Summary Bar */}
                  {selectedPackage && !catalogLoading && (() => {
                    const t = calculateTotal();
                    const itemCount =
                      Object.values(selectedAddOns).filter(Boolean).length +
                      Object.values(extrasQty).reduce((s, q) => s + (Number(q) > 0 ? 1 : 0), 0) +
                      customExtras.length +
                      (parseFloat(customChargeAmount) > 0 ? 1 : 0);
                    const addonsSum = (t.addOnsTotal || 0) + (t.extrasTotal || 0) + (t.customExtrasTotal || 0);
                    return (
                      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-[#E8E5DF] shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
                        data-testid="addons-summary-bar">
                        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
                          <div className="flex-1 min-w-[180px]">
                            <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">
                              {itemCount === 0 ? 'No add-ons selected' : `${itemCount} add-on${itemCount === 1 ? '' : 's'} selected`}
                            </p>
                            <p className="text-lg font-bold tabular-nums">
                              Add-ons: <span className="text-[#FF3B6B]">${addonsSum.toFixed(2)}</span>
                              <span className="text-xs text-[#6B6B7A] font-normal ml-2">+ Package ${(selectedPackage?.price || 0).toFixed(2)}</span>
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Estimated Total</p>
                            <p className="text-xl font-bold tabular-nums text-[#0FAB66]" data-testid="addons-summary-total">
                              ${t.total.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Legacy Step 2 body kept but hidden — customizations are now rendered above. */}
              {currentStep === 2 && false && (
                <div data-testid="step-addons-page-legacy">
                  <h2 className="text-2xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Customize Your Party
                  </h2>
                  <p className="text-[#6B6B7A] mb-6">Enhance {selectedPackage?.name || 'your package'} with add-ons, extras, designs and more. Skip if you'd like to keep it simple.</p>
                  {!selectedPackage && (
                    <div className="p-4 mb-6 rounded-xl bg-[#FFE8E5] border border-[#D4302A] text-sm text-[#D4302A]">
                      Please go back and select a package first.
                    </div>
                  )}

                  {/* Add-Ons Section */}
                  {selectedPackage && (
                    <div className="mt-2">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full bg-[#FFD166]/20 flex items-center justify-center">
                          <Sparkles className="w-5 h-5 text-[#B8860B]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                            Add-Ons
                          </h3>
                          <p className="text-sm text-[#6B6B7A]">Enhance your party experience</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {ADD_ONS.map((addon) => {
                          const isActive = addon.type === 'quantity'
                            ? (selectedAddOns[addon.id] || 0) > 0
                            : !!selectedAddOns[addon.id];
                          const qty = selectedAddOns[addon.id] || 0;

                          return (
                            <div
                              key={addon.id}
                              className={`p-4 rounded-xl border-2 transition-all ${
                                isActive
                                  ? 'border-[#FFD166] bg-[#FFD166]/5'
                                  : 'border-[#E8E5DF] hover:border-[#FFD166]/50'
                              }`}
                              data-testid={`addon-${addon.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <p className="font-semibold text-[#1E1E24] text-sm">{addon.name}</p>
                                  <p className="text-xs text-[#6B6B7A]">
                                    ${addon.price.toFixed(2)} {addon.unit === 'per guest' ? '/ guest' : addon.unit === 'handling fee' ? '(handling fee)' : ''}
                                  </p>
                                </div>

                                {addon.type === 'quantity' ? (
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => setAddOnQty(addon.id, qty - 1)}
                                      className="w-8 h-8 rounded-full border border-[#E8E5DF] flex items-center justify-center hover:bg-[#FFFDF8] text-[#6B6B7A]"
                                      data-testid={`addon-${addon.id}-minus`}
                                    >
                                      <Minus className="w-3 h-3" />
                                    </button>
                                    <span className="w-8 text-center font-semibold text-[#1E1E24]" data-testid={`addon-${addon.id}-qty`}>
                                      {qty}
                                    </span>
                                    <button
                                      onClick={() => setAddOnQty(addon.id, qty + 1)}
                                      className="w-8 h-8 rounded-full border border-[#E8E5DF] flex items-center justify-center hover:bg-[#FFFDF8] text-[#6B6B7A]"
                                      data-testid={`addon-${addon.id}-plus`}
                                    >
                                      <Plus className="w-3 h-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => toggleAddOn(addon.id)}
                                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                                      isActive
                                        ? 'bg-[#FFD166] text-white'
                                        : 'bg-[#F5F3EF] text-[#6B6B7A] hover:bg-[#FFD166]/20 hover:text-[#B8860B]'
                                    }`}
                                    data-testid={`addon-${addon.id}-toggle`}
                                  >
                                    {isActive ? 'Added' : 'Add'}
                                  </button>
                                )}
                              </div>

                              {/* Show calculated price for per_guest items */}
                              {addon.type === 'per_guest' && isActive && guestCount > 0 && (
                                <p className="text-xs text-[#B8860B] mt-2">
                                  {guestCount} guests x ${addon.price.toFixed(2)} = ${(addon.price * guestCount).toFixed(2)}
                                </p>
                              )}
                              {addon.type === 'quantity' && qty > 0 && (
                                <p className="text-xs text-[#B8860B] mt-2">
                                  {qty} x ${addon.price.toFixed(2)} = ${(addon.price * qty).toFixed(2)}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Predefined Extras & Add-Ons (Balloons, Cupcakes, Plushies, etc.) */}
                      <div className="mt-8">
                        <div className="flex items-center gap-3 mb-5">
                          <div className="w-10 h-10 rounded-full bg-[#FF3B6B]/15 flex items-center justify-center">
                            <Gift className="w-5 h-5 text-[#FF3B6B]" />
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                              Extras & Add-Ons
                            </h3>
                            <p className="text-sm text-[#6B6B7A]">Treats, candles, plushies & more — pick quantities</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="predefined-extras-grid">
                          {PREDEFINED_EXTRAS.map((item) => {
                            const qty = extrasQty[item.id] || 0;
                            const subtotal = qty * item.price;
                            return (
                              <div
                                key={item.id}
                                className={`p-4 rounded-xl border-2 transition-all ${
                                  qty > 0
                                    ? 'border-[#FF3B6B] bg-[#FF3B6B]/5'
                                    : 'border-[#E8E5DF] hover:border-[#FF3B6B]/40'
                                }`}
                                data-testid={`extra-${item.id}`}
                              >
                                <p className="font-semibold text-[#1E1E24] text-sm leading-tight min-h-[2.5rem]">
                                  {item.name}
                                </p>
                                <p className="text-xs text-[#6B6B7A] mb-3">
                                  ${item.price.toFixed(2)} <span className="text-[#9a9aa6]">· max {item.max}</span>
                                </p>

                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => setExtraQty(item.id, qty - 1, item.max)}
                                    className="w-7 h-7 rounded-full border border-[#E8E5DF] flex items-center justify-center hover:bg-[#FFFDF8] text-[#6B6B7A] disabled:opacity-40"
                                    disabled={qty <= 0}
                                    data-testid={`extra-${item.id}-minus`}
                                  >
                                    <Minus className="w-3 h-3" />
                                  </button>
                                  <Input
                                    type="number"
                                    min={0}
                                    max={item.max}
                                    value={qty}
                                    onChange={(e) => setExtraQty(item.id, parseInt(e.target.value, 10) || 0, item.max)}
                                    className="h-7 w-12 text-center text-sm p-1 rounded-md border-[#E8E5DF]"
                                    data-testid={`extra-${item.id}-qty`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setExtraQty(item.id, qty + 1, item.max)}
                                    className="w-7 h-7 rounded-full border border-[#E8E5DF] flex items-center justify-center hover:bg-[#FFFDF8] text-[#6B6B7A] disabled:opacity-40"
                                    disabled={qty >= item.max}
                                    data-testid={`extra-${item.id}-plus`}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </button>
                                </div>

                                {qty > 0 && (
                                  <p className="text-xs text-[#FF3B6B] font-semibold mt-2" data-testid={`extra-${item.id}-subtotal`}>
                                    = ${subtotal.toFixed(2)}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Custom Extras — dropdown picker (predefined catalog) */}
                      <div className="mt-8 bg-[#F7F9FF] rounded-xl p-5 border border-[#E8E5DF]">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-[#6366F1]" />
                            <h4 className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                              Custom Extras – Add Your Own Items
                            </h4>
                          </div>
                          <Button
                            type="button"
                            onClick={addCustomExtra}
                            size="sm"
                            className="bg-[#6366F1] hover:bg-[#4f52d4] text-white rounded-full text-xs h-8"
                            data-testid="add-custom-extra-btn"
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add Item
                          </Button>
                        </div>
                        <p className="text-xs text-[#6B6B7A] mb-3">
                          Pick from our catalog (balloons, cupcakes, plushies and more) — quantity 1–50 per line.
                        </p>

                        {pickerOpen && (
                          <div className="mb-3 rounded-lg bg-white border border-[#6366F1]/30 p-3" data-testid="custom-extra-picker">
                            <div className="grid grid-cols-12 gap-2 items-center">
                              <div className="col-span-7">
                                <Select value={pickerItemId} onValueChange={setPickerItemId}>
                                  <SelectTrigger className="bg-white rounded-md h-9 text-sm border-[#E8E5DF]" data-testid="custom-extra-select">
                                    <SelectValue placeholder="Choose an item…" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white">
                                    {CUSTOM_EXTRA_CATALOG.map(c => (
                                      <SelectItem
                                        key={c.id}
                                        value={c.id}
                                        disabled={customExtras.some(e => e.catalog_id === c.id)}
                                        data-testid={`custom-extra-option-${c.id}`}
                                      >
                                        {c.name} — ${c.price.toFixed(2)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-2">
                                <Input
                                  type="number" min="1" max="50"
                                  value={pickerQty}
                                  onChange={(ev) => setPickerQty(ev.target.value)}
                                  placeholder="Qty"
                                  className="rounded-md text-sm h-9 border-[#E8E5DF]"
                                  data-testid="custom-extra-picker-qty"
                                />
                              </div>
                              <div className="col-span-3 flex gap-1 justify-end">
                                <Button
                                  type="button" size="sm"
                                  onClick={confirmAddCustomExtra}
                                  disabled={!pickerItemId}
                                  className="rounded-full bg-[#6366F1] hover:bg-[#4f52d4] text-white text-xs h-8"
                                  data-testid="custom-extra-picker-confirm"
                                >
                                  Add
                                </Button>
                                <Button
                                  type="button" size="sm" variant="ghost"
                                  onClick={() => setPickerOpen(false)}
                                  className="text-[#6B6B7A] text-xs h-8"
                                  data-testid="custom-extra-picker-cancel"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}

                        {customExtras.length === 0 && !pickerOpen ? (
                          <p className="text-xs text-[#9a9aa6] italic">No custom items yet — click &quot;Add Item&quot; to pick from the menu.</p>
                        ) : (
                          <div className="space-y-2" data-testid="custom-extras-list">
                            {customExtras.map((e, idx) => {
                              const unit = parseFloat(e.unit_price) || 0;
                              const q = parseInt(e.quantity, 10) || 0;
                              const lineTotal = unit * q;
                              return (
                                <div key={e.id} className="grid grid-cols-12 gap-2 items-center bg-white rounded-md px-3 py-2 border border-[#E8E5DF]" data-testid={`custom-extra-row-${idx}`}>
                                  <div className="col-span-6 text-sm text-[#1E1E24] font-medium truncate" data-testid={`custom-extra-name-${idx}`}>
                                    {e.name}
                                  </div>
                                  <div className="col-span-2 text-xs text-[#6B6B7A]" data-testid={`custom-extra-price-${idx}`}>
                                    ${unit.toFixed(2)}/ea
                                  </div>
                                  <div className="col-span-2 flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => updateCustomExtraQty(e.id, q - 1)}
                                      className="w-7 h-7 rounded-full border border-[#E8E5DF] flex items-center justify-center hover:bg-[#FFFDF8] text-[#6B6B7A] disabled:opacity-40"
                                      disabled={q <= 1}
                                      data-testid={`custom-extra-minus-${idx}`}
                                    >
                                      <Minus className="w-3 h-3" />
                                    </button>
                                    <Input
                                      type="number" min="1" max="50"
                                      value={q}
                                      onChange={(ev) => updateCustomExtraQty(e.id, ev.target.value)}
                                      className="h-7 w-10 text-center text-sm p-1 rounded-md border-[#E8E5DF]"
                                      data-testid={`custom-extra-qty-${idx}`}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => updateCustomExtraQty(e.id, q + 1)}
                                      className="w-7 h-7 rounded-full border border-[#E8E5DF] flex items-center justify-center hover:bg-[#FFFDF8] text-[#6B6B7A] disabled:opacity-40"
                                      disabled={q >= 50}
                                      data-testid={`custom-extra-plus-${idx}`}
                                    >
                                      <Plus className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <div className="col-span-1 text-xs text-[#6366F1] font-semibold text-right" data-testid={`custom-extra-total-${idx}`}>
                                    ${lineTotal.toFixed(2)}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeCustomExtra(e.id)}
                                    className="col-span-1 w-8 h-8 rounded-full text-[#FF3B6B] hover:bg-[#FF3B6B]/10 flex items-center justify-center justify-self-end"
                                    data-testid={`custom-extra-remove-${idx}`}
                                    aria-label="Remove"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Design Studio — available whenever a print add-on is on the booking */}
                      {(() => {
                        const printAddons = Object.keys(PRINT_GARMENT_MAP).filter(id => (extrasQty[id] || 0) > 0);
                        const canDesign = printAddons.length > 0;
                        return (
                          <div className="mt-6 rounded-xl p-5 border-2 border-dashed"
                            style={{ borderColor: canDesign ? '#FF3B6B' : '#E8E5DF', backgroundColor: canDesign ? '#FFF0F3' : '#FFFDF8' }}
                            data-testid="design-studio-section">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div className="flex items-start gap-2 min-w-[240px]">
                                <Palette className="w-5 h-5 text-[#FF3B6B] mt-0.5" />
                                <div>
                                  <h4 className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                                    Design Studio
                                  </h4>
                                  <p className="text-xs text-[#6B6B7A]">
                                    {canDesign
                                      ? 'Design each printed item now — upload a photo or pick a template, preview live.'
                                      : 'Add Print-a-Cap, T-Shirt or Mug above to unlock the Design Studio.'}
                                  </p>
                                </div>
                              </div>
                              <Button
                                type="button"
                                onClick={() => openDesignStudio(PRINT_GARMENT_MAP[printAddons[0]] || 'tshirt')}
                                disabled={!canDesign}
                                className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white h-10 px-5 disabled:opacity-50"
                                data-testid="open-design-studio-btn"
                              >
                                <Sparkles className="w-4 h-4 mr-1.5" />
                                {designs.length > 0 ? 'Add another design' : 'Open Design Studio'}
                              </Button>
                            </div>

                            {designs.length > 0 && (
                              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="saved-designs-list">
                                {designs.map((d) => (
                                  <div key={d.id} className="relative bg-white rounded-xl border border-[#E8E5DF] p-2" data-testid={`saved-design-${d.id}`}>
                                    <div className="flex items-center gap-2">
                                      <span className="text-2xl" aria-hidden>
                                        {d.garment === 'cap' ? '🧢' : d.garment === 'mug' ? '☕' : '👕'}
                                      </span>
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold text-[#1E1E24] truncate capitalize">{d.garment === 'tshirt' ? 'T-Shirt' : d.garment}</p>
                                        <p className="text-[10px] text-[#6B6B7A] truncate">
                                          {d.template_id ? `Template: ${d.template_id}` : 'Uploaded image'}
                                          {d.label ? ` · ${d.label}` : ''}
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removeDesign(d.id)}
                                      className="absolute top-1 right-1 w-6 h-6 rounded-full text-[#FF3B6B] hover:bg-[#FF3B6B]/10 flex items-center justify-center"
                                      aria-label="Remove design"
                                      data-testid={`remove-design-${d.id}`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Custom Charge */}
                      <div className="mt-6 bg-[#FFFDF8] rounded-xl p-5 border border-[#E8E5DF]">
                        <div className="flex items-center gap-2 mb-3">
                          <DollarSign className="w-5 h-5 text-[#06D6A0]" />
                          <h4 className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Custom Extra (optional)</h4>
                        </div>
                        <p className="text-xs text-[#6B6B7A] mb-3">Add any extra item or service not included above (e.g., extra hour, custom fruit shapes, cleaning fee)</p>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="col-span-2">
                            <Input value={customChargeDesc} onChange={e => setCustomChargeDesc(e.target.value)}
                              placeholder="Description (e.g., Extra hour)" className="rounded-xl border-[#E8E5DF] text-sm" data-testid="custom-charge-desc" />
                          </div>
                          <div>
                            <Input type="number" step="0.01" min="0" value={customChargeAmount} onChange={e => setCustomChargeAmount(e.target.value)}
                              placeholder="$ Amount" className="rounded-xl border-[#E8E5DF] text-sm" data-testid="custom-charge-amount" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Date & Time */}
              {currentStep === 3 && (
                <div>
                  <h2 className="text-2xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Pick Your Date & Time
                  </h2>
                  <p className="text-[#6B6B7A] mb-8">Select an available date and time slot for your party</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <Label className="text-[#1E1E24] font-semibold mb-3 block">Select Date</Label>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={isDateDisabled}
                        className="rounded-xl border border-[#E8E5DF] p-3"
                        data-testid="booking-calendar"
                      />
                    </div>

                    <div>
                      <Label className="text-[#1E1E24] font-semibold mb-3 block">Select Time</Label>
                      {selectedDate ? (
                        <div className="space-y-3">
                          {timeSlots.map((slot) => {
                            const isAvailable = availableSlots.includes(slot);
                            const slotLabel = slot === '10:00' ? '10:00 AM' : slot === '13:00' ? '1:00 PM' : '4:00 PM';
                            return (
                              <button
                                key={slot}
                                onClick={() => isAvailable && setSelectedTime(slot)}
                                disabled={!isAvailable}
                                className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                                  selectedTime === slot
                                    ? 'border-[#FF3B6B] bg-[#FF3B6B]/5 text-[#FF3B6B]'
                                    : isAvailable
                                    ? 'border-[#E8E5DF] hover:border-[#FF3B6B]/50 text-[#1E1E24]'
                                    : 'border-[#E8E5DF] bg-gray-50 text-[#6B6B7A] cursor-not-allowed'
                                }`}
                                data-testid={`time-slot-${slot}`}
                              >
                                <Clock className="w-5 h-5" />
                                <span className="font-medium">{slotLabel}</span>
                                {!isAvailable && <span className="ml-auto text-sm">(Booked)</span>}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-[#6B6B7A] p-4 border border-dashed border-[#E8E5DF] rounded-xl text-center">
                          Please select a date first
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Guest Details */}
              {currentStep === 4 && (
                <div>
                  <h2 className="text-2xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Guest Details
                  </h2>
                  <p className="text-[#6B6B7A] mb-8">Tell us about the birthday child and guests</p>

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="childName" className="text-[#1E1E24] font-semibold">Birthday Child's Name *</Label>
                        <Input
                          id="childName"
                          value={childName}
                          onChange={(e) => setChildName(e.target.value)}
                          placeholder="Enter child's name"
                          className="mt-2 rounded-xl border-[#E8E5DF] focus:ring-[#FF3B6B] focus:border-[#FF3B6B]"
                          data-testid="input-child-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="childAge" className="text-[#1E1E24] font-semibold">Child's Age *</Label>
                        <Select value={childAge} onValueChange={setChildAge}>
                          <SelectTrigger className="mt-2 rounded-xl border-[#E8E5DF]" data-testid="select-child-age">
                            <SelectValue placeholder="Select age" />
                          </SelectTrigger>
                          <SelectContent>
                            {[...Array(15)].map((_, i) => (
                              <SelectItem key={i + 1} value={String(i + 1)}>{i + 1} years old</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label className="text-[#1E1E24] font-semibold">Number of Guests *</Label>
                      <div className="flex items-center gap-4 mt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
                          className="rounded-full w-10 h-10 p-0"
                          data-testid="guest-decrease"
                        >
                          -
                        </Button>
                        <div className="flex items-center gap-2 px-6 py-3 rounded-xl border border-[#E8E5DF] bg-white">
                          <Users className="w-5 h-5 text-[#6B6B7A]" />
                          <span className="font-semibold text-[#1E1E24] text-lg" data-testid="guest-count">{guestCount}</span>
                          <span className="text-[#6B6B7A]">guests</span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setGuestCount(Math.min(selectedPackage?.guests || 12, guestCount + 1))}
                          className="rounded-full w-10 h-10 p-0"
                          data-testid="guest-increase"
                        >
                          +
                        </Button>
                      </div>
                      {selectedPackage && (
                        <p className="text-sm text-[#6B6B7A] mt-2">
                          Maximum {selectedPackage.guests} guests for {selectedPackage.name}
                        </p>
                      )}
                    </div>

                    {/* Edible Arrangements Integration */}
                    <div className="bg-[#FFF4D6] rounded-2xl p-6 border border-[#FFD166]">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#FFD166]/30 flex items-center justify-center flex-shrink-0">
                          <ExternalLink className="w-6 h-6 text-[#B8860B]" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                            Build Your DIY Edible Bouquet
                          </h4>
                          <p className="text-sm text-[#6B6B7A] mb-4">
                            Want custom fruit selections? Visit Edible Arrangements to design your perfect bouquet, 
                            then paste your selection notes below.
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => window.open('https://www.ediblearrangements.ca', '_blank')}
                            className="border-[#FFD166] text-[#B8860B] hover:bg-[#FFD166] hover:text-white rounded-full mb-4"
                            data-testid="edible-arrangements-link"
                          >
                            Visit Edible Arrangements
                            <ExternalLink className="w-4 h-4 ml-2" />
                          </Button>
                          <Textarea
                            value={edibleNotes}
                            onChange={(e) => setEdibleNotes(e.target.value)}
                            placeholder="Paste your bouquet design notes or special requests here (optional)"
                            className="rounded-xl border-[#E8E5DF] focus:ring-[#FF3B6B] focus:border-[#FF3B6B]"
                            rows={3}
                            data-testid="edible-notes"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Contact Information */}
              {currentStep === 5 && (
                <div>
                  <h2 className="text-2xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Your Information
                  </h2>
                  <p className="text-[#6B6B7A] mb-8">How can we reach you about this booking?</p>

                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="customerName" className="text-[#1E1E24] font-semibold">Your Full Name *</Label>
                      <Input
                        id="customerName"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="Enter your name"
                        className="mt-2 rounded-xl border-[#E8E5DF] focus:ring-[#FF3B6B] focus:border-[#FF3B6B]"
                        data-testid="input-customer-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="customerEmail" className="text-[#1E1E24] font-semibold">Email Address *</Label>
                      <Input
                        id="customerEmail"
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="mt-2 rounded-xl border-[#E8E5DF] focus:ring-[#FF3B6B] focus:border-[#FF3B6B]"
                        data-testid="input-customer-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="customerPhone" className="text-[#1E1E24] font-semibold">Phone Number *</Label>
                      <Input
                        id="customerPhone"
                        type="tel"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        placeholder="(123) 456-7890"
                        className="mt-2 rounded-xl border-[#E8E5DF] focus:ring-[#FF3B6B] focus:border-[#FF3B6B]"
                        data-testid="input-customer-phone"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-8 pt-6 border-t border-[#E8E5DF]">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  className="rounded-full border-[#E8E5DF]"
                  data-testid="btn-back"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full btn-hover"
                  data-testid="btn-next"
                >
                  {currentStep === 5 ? 'Continue to Allergy Form' : 'Next'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-[#E8E5DF] p-6 sticky top-24">
              <h3 className="text-lg font-semibold text-[#1E1E24] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Booking Summary
              </h3>

              {selectedPackage ? (
                <div className="space-y-4">
                  <div className="p-4 bg-[#FFFDF8] rounded-xl">
                    <p className="font-semibold text-[#1E1E24]">{selectedPackage.name}</p>
                    <p className="text-sm text-[#6B6B7A]">{selectedPackage.duration} | Up to {selectedPackage.guests} guests</p>
                  </div>

                  {selectedDate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6B6B7A]">Date</span>
                      <span className="text-[#1E1E24] font-medium">
                        {selectedDate.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  )}

                  {selectedTime && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6B6B7A]">Time</span>
                      <span className="text-[#1E1E24] font-medium">
                        {selectedTime === '10:00' ? '10:00 AM' : selectedTime === '13:00' ? '1:00 PM' : '4:00 PM'}
                      </span>
                    </div>
                  )}

                  {guestCount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6B6B7A]">Guests</span>
                      <span className="text-[#1E1E24] font-medium">{guestCount}</span>
                    </div>
                  )}

                  <div className="border-t border-[#E8E5DF] pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6B6B7A]">Package</span>
                      <span className="text-[#1E1E24]">${selectedPackage.price.toFixed(2)}</span>
                    </div>

                    {/* iter-152 — Category-grouped add-on breakdown.
                        Walks the fetched catalog in display order and renders a
                        heading for each category that has at least one selected
                        line item. Falls back to the legacy Add-Ons/Extras/Custom
                        Extras blocks when the catalog is still loading (initial
                        render, offline, or admin-drained categories). */}
                    {(catalog?.categories?.length || 0) > 0 ? (
                      <>
                        {catalog.categories.map((cat) => {
                          const lines = [];
                          (cat.items || []).forEach((it) => {
                            if (TOGGLE_ITEM_IDS.has(it.item_id)) {
                              // Toggle items (DIY Cake, Extra 30 min) use selectedAddOns
                              if (selectedAddOns[it.item_id]) {
                                lines.push({
                                  key: it.item_id,
                                  label: it.name,
                                  qty: 1,
                                  amount: Number(it.price) || 0,
                                });
                              }
                            } else {
                              const q = extrasQty[it.item_id] || 0;
                              if (q > 0) {
                                lines.push({
                                  key: it.item_id,
                                  label: it.name,
                                  qty: q,
                                  amount: (Number(it.price) || 0) * q,
                                });
                              }
                            }
                          });
                          if (lines.length === 0) return null;
                          const catTotal = lines.reduce((s, l) => s + l.amount, 0);
                          return (
                            <div key={cat.category_id}
                              className="pt-1"
                              data-testid={`summary-cat-${cat.category_id}`}>
                              <div className="flex justify-between text-sm">
                                <span className="text-[#B8860B] font-medium">
                                  {cat.icon} {cat.name}
                                </span>
                                <span className="text-[#B8860B] font-medium tabular-nums">
                                  ${catTotal.toFixed(2)}
                                </span>
                              </div>
                              {lines.map((line) => (
                                <div key={line.key}
                                  className="flex justify-between text-xs pl-4"
                                  data-testid={`summary-line-${line.key}`}>
                                  <span className="text-[#6B6B7A] truncate mr-2">
                                    {line.label}{line.qty > 1 ? ` × ${line.qty}` : ''}
                                  </span>
                                  <span className="text-[#6B6B7A] tabular-nums flex-shrink-0">
                                    ${line.amount.toFixed(2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          );
                        })}

                        {/* Custom Extras — always its own section, even for
                            catalog-driven layout, since these are user-defined
                            free-text items not present in the catalog. */}
                        {customExtras.length > 0 && (() => {
                          const validCustoms = customExtras
                            .map((e) => {
                              const p = parseFloat(e.unit_price) || 0;
                              const q = parseInt(e.quantity, 10) || 0;
                              const name = (e.name || '').trim();
                              return (p > 0 && q > 0 && name) ? { ...e, _p: p, _q: q, _amount: p * q } : null;
                            })
                            .filter(Boolean);
                          if (validCustoms.length === 0) return null;
                          const customTotal = validCustoms.reduce((s, v) => s + v._amount, 0);
                          return (
                            <div className="pt-1" data-testid="summary-cat-custom">
                              <div className="flex justify-between text-sm">
                                <span className="text-[#6366F1] font-medium">➕ Custom Extras</span>
                                <span className="text-[#6366F1] font-medium tabular-nums">${customTotal.toFixed(2)}</span>
                              </div>
                              {validCustoms.map((e) => (
                                <div key={e.id} className="flex justify-between text-xs pl-4"
                                  data-testid={`summary-line-custom-${e.id}`}>
                                  <span className="text-[#6B6B7A] truncate mr-2">
                                    {e.name}{e._q > 1 ? ` × ${e._q}` : ''}
                                  </span>
                                  <span className="text-[#6B6B7A] tabular-nums flex-shrink-0">${e._amount.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}

                        {/* Grand Add-ons subtotal (spec: "The add-ons total
                            matches the sum of all line totals"). */}
                        {(pricing.addOnsTotal + pricing.extrasTotal + pricing.customExtrasTotal) > 0 && (
                          <div className="flex justify-between text-sm pt-1 border-t border-dashed border-[#E8E5DF] mt-1"
                            data-testid="summary-addons-total">
                            <span className="text-[#1E1E24] font-semibold">Add-ons Total</span>
                            <span className="text-[#1E1E24] font-semibold tabular-nums">
                              ${(pricing.addOnsTotal + pricing.extrasTotal + pricing.customExtrasTotal).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Legacy fallback (unchanged) when catalog not loaded */}
                        {pricing.addOnsTotal > 0 && (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-[#B8860B]">Add-Ons</span>
                              <span className="text-[#B8860B]">${pricing.addOnsTotal.toFixed(2)}</span>
                            </div>
                            {ADD_ONS.map(addon => {
                              const val = selectedAddOns[addon.id];
                              if (!val) return null;
                              let cost = 0;
                              let label = addon.name;
                              if (addon.type === 'toggle' && val === true) { cost = addon.price; }
                              else if (addon.type === 'quantity' && val > 0) { cost = addon.price * val; label = `${addon.name} x${val}`; }
                              else if (addon.type === 'per_guest' && val === true) { cost = addon.price * guestCount; label = `${addon.name} (${guestCount})`; }
                              else return null;
                              return (
                                <div key={addon.id} className="flex justify-between text-xs pl-3">
                                  <span className="text-[#6B6B7A]">{label}</span>
                                  <span className="text-[#6B6B7A]">${cost.toFixed(2)}</span>
                                </div>
                              );
                            })}
                          </>
                        )}
                        {pricing.extrasTotal > 0 && (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-[#FF3B6B]">Extras & Add-Ons</span>
                              <span className="text-[#FF3B6B]">${pricing.extrasTotal.toFixed(2)}</span>
                            </div>
                            {PREDEFINED_EXTRAS.map(item => {
                              const q = extrasQty[item.id] || 0;
                              if (q <= 0) return null;
                              return (
                                <div key={item.id} className="flex justify-between text-xs pl-3">
                                  <span className="text-[#6B6B7A]">{item.name} x{q}</span>
                                  <span className="text-[#6B6B7A]">${(item.price * q).toFixed(2)}</span>
                                </div>
                              );
                            })}
                          </>
                        )}
                        {pricing.customExtrasTotal > 0 && (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-[#6366F1]">Custom Extras</span>
                              <span className="text-[#6366F1]">${pricing.customExtrasTotal.toFixed(2)}</span>
                            </div>
                            {customExtras.map((e, i) => {
                              const p = parseFloat(e.unit_price) || 0;
                              const q = parseInt(e.quantity, 10) || 0;
                              if (p <= 0 || q <= 0 || !(e.name || '').trim()) return null;
                              return (
                                <div key={e.id} className="flex justify-between text-xs pl-3">
                                  <span className="text-[#6B6B7A]">{e.name} x{q}</span>
                                  <span className="text-[#6B6B7A]">${(p * q).toFixed(2)}</span>
                                </div>
                              );
                            })}
                          </>
                        )}
                      </>
                    )}
                    {parseFloat(customChargeAmount) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-[#06D6A0]">{customChargeDesc || 'Custom Extra'}</span>
                        <span className="text-[#06D6A0]">${parseFloat(customChargeAmount).toFixed(2)}</span>
                      </div>
                    )}
                    {designs.length > 0 && (
                      <div className="flex justify-between text-xs italic text-[#FF3B6B] pt-1" data-testid="summary-designs-count">
                        <span>Custom designs</span>
                        <span>{designs.length} attached</span>
                      </div>
                    )}

                    {/* Promo code */}
                    <div className="pt-3 mt-2 border-t border-dashed border-[#E8E5DF]">
                      {appliedPromo ? (
                        <div className="flex items-center justify-between gap-2 p-2 rounded-xl bg-[#06D6A0]/10" data-testid="promo-applied">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-[#058B69] truncate">
                              {appliedPromo.code} applied
                              {appliedPromo.type === 'percent'
                                ? <span className="font-normal"> — {appliedPromo.value}% off</span>
                                : <span className="font-normal"> — ${appliedPromo.value.toFixed(2)} off</span>}
                            </p>
                            {appliedPromo.description && <p className="text-[10px] text-[#058B69]/80 truncate">{appliedPromo.description}</p>}
                          </div>
                          <button
                            type="button"
                            onClick={removePromo}
                            className="text-xs text-[#058B69] hover:text-[#FF3B6B] underline"
                            data-testid="promo-remove"
                          >
                            remove
                          </button>
                        </div>
                      ) : (
                        <div data-testid="promo-input-wrapper">
                          <label className="text-xs text-[#6B6B7A] block mb-1.5">Promo code</label>
                          <div className="flex gap-2">
                            <Input
                              value={promoInput}
                              onChange={(e) => { setPromoInput(e.target.value.toUpperCase().slice(0, 32)); setPromoError(''); }}
                              placeholder="Enter code"
                              className="h-9 rounded-xl border-[#E8E5DF] font-mono text-sm flex-1"
                              data-testid="promo-input"
                            />
                            <Button
                              type="button"
                              onClick={() => validatePromo()}
                              disabled={promoValidating || !promoInput.trim()}
                              className="h-9 px-3 rounded-xl bg-[#1E1E24] hover:bg-[#2E2E34] text-white text-xs"
                              data-testid="promo-apply"
                            >
                              {promoValidating ? '…' : 'Apply'}
                            </Button>
                          </div>
                          {promoError && <p className="text-xs text-[#FF3B6B] mt-1" data-testid="promo-error">{promoError}</p>}
                        </div>
                      )}
                    </div>

                    {/* Referral code */}
                    <div className="pt-3 mt-2 border-t border-dashed border-[#E8E5DF]" data-testid="referral-section">
                      {appliedReferral ? (
                        <div className="flex items-center justify-between gap-2 p-2 rounded-xl bg-[#6366F1]/10" data-testid="referral-applied">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-[#4F46E5] truncate">
                              Referred by {appliedReferral.referrer_first_name}
                            </p>
                            <p className="text-[10px] text-[#4F46E5]/80 truncate font-mono">{appliedReferral.code}</p>
                          </div>
                          <button
                            type="button"
                            onClick={removeReferral}
                            className="text-xs text-[#4F46E5] hover:text-[#FF3B6B] underline"
                            data-testid="referral-remove"
                          >
                            remove
                          </button>
                        </div>
                      ) : (
                        <div data-testid="referral-input-wrapper">
                          <label className="text-xs text-[#6B6B7A] block mb-1.5">Referral code <span className="text-[#6B6B7A]/60">(optional)</span></label>
                          <div className="flex gap-2">
                            <Input
                              value={referralInput}
                              onChange={(e) => { setReferralInput(e.target.value.toUpperCase().slice(0, 20)); setReferralError(''); }}
                              placeholder="REF-XXXXXX"
                              className="h-9 rounded-xl border-[#E8E5DF] font-mono text-sm flex-1"
                              data-testid="referral-input"
                            />
                            <Button
                              type="button"
                              onClick={() => validateReferral()}
                              disabled={referralValidating || !referralInput.trim()}
                              className="h-9 px-3 rounded-xl bg-[#6366F1] hover:bg-[#4F46E5] text-white text-xs"
                              data-testid="referral-apply"
                            >
                              {referralValidating ? '…' : 'Apply'}
                            </Button>
                          </div>
                          {referralError && <p className="text-xs text-[#FF3B6B] mt-1" data-testid="referral-error">{referralError}</p>}
                        </div>
                      )}
                    </div>

                    {/* Gift card */}
                    <div className="pt-3 mt-2 border-t border-dashed border-[#E8E5DF]" data-testid="gift-card-section">
                      {appliedGiftCard ? (
                        <div className="flex items-center justify-between gap-2 p-2 rounded-xl bg-[#FB923C]/10" data-testid="gift-card-applied">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-[#C2410C] truncate">
                              <Gift className="w-3 h-3 inline mr-1" />
                              Gift card {appliedGiftCard.code}
                            </p>
                            <p className="text-[10px] text-[#C2410C]/80 truncate">
                              ${(appliedGiftCard.balance_remaining || 0).toFixed(2)} available
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={removeGiftCard}
                            className="text-xs text-[#C2410C] hover:text-[#FF3B6B] underline"
                            data-testid="gift-card-remove"
                          >
                            remove
                          </button>
                        </div>
                      ) : (
                        <div data-testid="gift-card-input-wrapper">
                          <label className="text-xs text-[#6B6B7A] block mb-1.5">
                            Gift card code <span className="text-[#6B6B7A]/60">(optional)</span>
                          </label>
                          <div className="flex gap-2">
                            <Input
                              value={giftCardInput}
                              onChange={(e) => { setGiftCardInput(e.target.value.toUpperCase().slice(0, 32)); setGiftCardError(''); }}
                              placeholder="GIFT-XXXXXX"
                              className="h-9 rounded-xl border-[#E8E5DF] font-mono text-sm flex-1"
                              data-testid="gift-card-input"
                            />
                            <Button
                              type="button"
                              onClick={() => validateGiftCard()}
                              disabled={giftCardValidating || !giftCardInput.trim()}
                              className="h-9 px-3 rounded-xl bg-[#FB923C] hover:bg-[#EA580C] text-white text-xs"
                              data-testid="gift-card-apply"
                            >
                              {giftCardValidating ? '…' : 'Apply'}
                            </Button>
                          </div>
                          {giftCardError && <p className="text-xs text-[#FF3B6B] mt-1" data-testid="gift-card-error">{giftCardError}</p>}
                        </div>
                      )}
                    </div>

                    {/* Loyalty vouchers (logged-in customers only) */}
                    {loyaltyVouchers.length > 0 && (
                      <div className="pt-3 mt-2 border-t border-dashed border-[#E8E5DF]" data-testid="loyalty-vouchers">
                        <label className="text-xs text-[#6B6B7A] block mb-1.5 flex items-center gap-1">
                          <Gift className="w-3 h-3 text-[#FF3B6B]" /> Your loyalty rewards
                        </label>
                        <div className="space-y-1.5">
                          {loyaltyVouchers.map((v) => {
                            const isFreebie = v.type === 'freebie';
                            const freebieApplied = isFreebie && appliedFreebieVoucher?.voucher_id === v.voucher_id;
                            const discountApplied = !isFreebie && v.discount_code && appliedPromo?.code === v.discount_code;
                            const applied = freebieApplied || discountApplied;
                            return (
                              <div key={v.voucher_id} className={`flex items-center justify-between gap-2 p-2 rounded-xl ${applied ? 'bg-[#06D6A0]/10' : 'bg-[#FFFDF8] border border-[#E8E5DF]'}`}
                                   data-testid={`loyalty-voucher-${v.voucher_id}`}>
                                <div className="min-w-0">
                                  <p className="text-xs font-semibold text-[#1E1E24] truncate">{v.reward_name}</p>
                                  {v.discount_code && <p className="text-[10px] font-mono text-[#6B6B7A]">{v.discount_code}</p>}
                                </div>
                                {applied ? (
                                  <button
                                    type="button"
                                    onClick={() => isFreebie ? removeFreebieVoucher() : removePromo()}
                                    className="text-xs text-[#058B69] hover:text-[#FF3B6B] underline"
                                    data-testid={`loyalty-remove-${v.voucher_id}`}
                                  >
                                    remove
                                  </button>
                                ) : (
                                  <Button
                                    type="button"
                                    onClick={() => isFreebie ? applyFreebieVoucher(v) : applyDiscountVoucher(v)}
                                    className="h-7 px-2 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-[11px]"
                                    data-testid={`loyalty-apply-${v.voucher_id}`}
                                  >
                                    Apply
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {appliedFreebieVoucher && (
                          <p className="text-[10px] text-[#6B6B7A] mt-1.5 italic">
                            Freebie credit applies to 1 unit of the matching item.
                          </p>
                        )}
                      </div>
                    )}

                    <div className="flex justify-between text-sm pt-3">
                      <span className="text-[#6B6B7A]">Subtotal</span>
                      <span className="text-[#1E1E24]">${pricing.preDiscountSubtotal.toFixed(2)}</span>
                    </div>
                    {pricing.discountAmount > 0 && (
                      <div className="flex justify-between text-sm" data-testid="summary-discount">
                        <span className="text-[#06D6A0]">Discount ({appliedPromo?.code})</span>
                        <span className="text-[#06D6A0]">− ${pricing.discountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {pricing.specialOfferAmount > 0 && (
                      <div className="flex justify-between text-sm" data-testid="summary-special-offer">
                        <span className="text-[#FF3B6B]">★ {pricing.specialOffer?.title || 'Special Offer'}</span>
                        <span className="text-[#FF3B6B]">− ${pricing.specialOfferAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {pricing.freebieAmount > 0 && (
                      <div className="flex justify-between text-sm" data-testid="summary-freebie">
                        <span className="text-[#06D6A0]">Loyalty freebie ({appliedFreebieVoucher?.reward_name})</span>
                        <span className="text-[#06D6A0]">− ${pricing.freebieAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {pricing.giftCardAmount > 0 && (
                      <div className="flex justify-between text-sm" data-testid="summary-gift-card">
                        <span className="text-[#FB923C]">Gift card ({appliedGiftCard?.code})</span>
                        <span className="text-[#FB923C]">− ${pricing.giftCardAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6B6B7A]">Tax (13%)</span>
                      <span className="text-[#1E1E24]">${pricing.tax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-lg pt-2 border-t border-[#E8E5DF]">
                      <span className="text-[#1E1E24]">Total</span>
                      <span className="text-[#FF3B6B]" data-testid="summary-total">${pricing.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm bg-[#06D6A0]/10 p-3 rounded-xl mt-4">
                      <span className="text-[#06D6A0] font-medium">Deposit (50%)</span>
                      <span className="text-[#06D6A0] font-semibold">${pricing.deposit.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-[#6B6B7A] text-center py-8">Select a package to see pricing</p>
              )}
            </div>
          </div>
        </div>

        {/* ─── Booking Page FAQ ─── */}
        <div className="mt-10" data-testid="booking-faq">
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle className="w-5 h-5 text-[#FF3B6B]" />
            <h2 className="text-2xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Frequently asked questions
            </h2>
          </div>
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-2 sm:p-4">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="cancellation" data-testid="faq-cancellation">
                <AccordionTrigger className="text-left text-[#1E1E24] font-medium">
                  What is the cancellation & refund policy?
                </AccordionTrigger>
                <AccordionContent className="text-[#6B6B7A] text-sm">
                  Full refunds are issued for cancellations made at least <strong>24 hours</strong> before your party time. Within 24 hours, the 50% deposit is non-refundable but we can reschedule once at no extra cost. Weather and emergency cancellations are reviewed case-by-case.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="allergies" data-testid="faq-allergies">
                <AccordionTrigger className="text-left text-[#1E1E24] font-medium">
                  How do you handle allergies & dietary needs?
                </AccordionTrigger>
                <AccordionContent className="text-[#6B6B7A] text-sm">
                  Every booking includes an allergy & consent form where you can list allergens, medical conditions, and emergency contacts. We offer gluten-free / dairy-free cupcakes, nut-free prep, and custom accommodations — just add a note on your order or email us before the party.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="food" data-testid="faq-food">
                <AccordionTrigger className="text-left text-[#1E1E24] font-medium">
                  Can we bring our own food, or do you provide it?
                </AccordionTrigger>
                <AccordionContent className="text-[#6B6B7A] text-sm">
                  Depends on your package. <strong>Create Party</strong> lets you bring your own food. <strong>Celebrate Party</strong> includes pizza & cupcakes. <strong>Signature Edible Party</strong> is fully catered. You can always add pizza, cupcakes or a custom cake from the add-ons list.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="diy-fruit" data-testid="faq-diy-fruit">
                <AccordionTrigger className="text-left text-[#1E1E24] font-medium">
                  What's included in the DIY Fruit Bouquet?
                </AccordionTrigger>
                <AccordionContent className="text-[#6B6B7A] text-sm">
                  Each child builds a small fruit bouquet at their station using pre-washed seasonal fruit, skewers, and a take-home ribboned vase. Our host demos techniques and cleans up at the end. Nut-free by default; allergy notes from your form are respected.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="printing" data-testid="faq-printing">
                <AccordionTrigger className="text-left text-[#1E1E24] font-medium">
                  How does printing on caps & T-shirts work?
                </AccordionTrigger>
                <AccordionContent className="text-[#6B6B7A] text-sm">
                  Kids design their artwork in our on-site Design Studio (or upload one earlier). We heat-press the design onto a pre-included cap, T-shirt or mug while the party continues — each child leaves with their finished piece. Choose BYO Printing if you prefer to bring your own garment.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
      <DesignStudioModal
        open={designOpen}
        onOpenChange={setDesignOpen}
        initialGarment={designInitialGarment}
        childName={childName}
        onSave={onDesignSaved}
      />
    </div>
  );
};

export default BookingFlow;
